import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Flame,
  Layers,
  TrendingDown,
  CalendarClock,
  Loader2,
  ChevronRight,
} from "lucide-react";
import { launchpadApi } from "@/lib/api";
import useRefreshOnFocus from "@/lib/useRefreshOnFocus";

// StudioInsightsWidget — Feb 2026.
//
// Launchpad-side surface for the Studio Resource Manager insight engine.
// Pulls the top 2 ranked insights for the next 14 days and lets the admin
// jump straight to the Studio page focused on the offending person/date.

const KIND_META = {
  stack:    { icon: Layers,        tint: "text-amber-400",  bg: "bg-amber-500/10",  bar: "#fbbf24" },
  overload: { icon: Flame,         tint: "text-rose-400",   bg: "bg-rose-500/10",   bar: "#fb7185" },
  streak:   { icon: CalendarClock, tint: "text-orange-400", bg: "bg-orange-500/10", bar: "#fb923c" },
  light:    { icon: TrendingDown,  tint: "text-teal-300",   bg: "bg-teal-500/10",   bar: "#5eead4" },
};

export default function StudioInsightsWidget({ isAdmin }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useRefreshOnFocus(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    launchpadApi
      .getStudioInsights({ days: 14, limit: 2 })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAdmin]);

  // Non-admins never see this widget.
  if (!isAdmin) return null;

  if (loading) {
    return (
      <article
        data-testid="studio-insights-widget-loading"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5 flex items-center gap-2 text-zinc-500 text-sm"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading studio insights…
      </article>
    );
  }

  if (!data) return null;
  const insights = data.insights || [];

  const openStudio = (ins) => {
    const qs = new URLSearchParams();
    if (ins?.user_id) qs.set("focus", ins.user_id);
    if (ins?.iso_date) qs.set("date", ins.iso_date);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    navigate(`/studio${suffix}`);
  };

  // Empty state — only render the celebratory card if there's actually a team
  // with tasks to evaluate (otherwise stay silent so a brand-new account
  // doesn't see a misleading "all balanced" message).
  if (insights.length === 0) {
    if ((data.people_count || 0) === 0 || (data.tasks_count || 0) === 0) {
      return null;
    }
    return (
      <article
        data-testid="studio-insights-widget"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
        style={{ borderLeftColor: "#5eead4", borderLeftWidth: 3 }}
      >
        <div className="text-overline text-teal-400/80 mb-1 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Studio · next {data?.window?.days || 14} days
        </div>
        <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
          Your roster is balanced.
        </h2>
        <p className="text-xs text-zinc-500 mt-1">
          No overload, no stacked sub-day tasks, no burnout streaks.{" "}
          <button
            type="button"
            onClick={() => navigate("/studio")}
            className="text-amber-500/90 hover:text-amber-400 transition-colors"
          >
            Open Studio →
          </button>
        </p>
      </article>
    );
  }

  // Border color = severity of the top insight (warns punch harder).
  const topSeverity = insights[0]?.severity;
  const borderColor = topSeverity === "warn" ? "#fb7185" : "#fbbf24";

  return (
    <article
      data-testid="studio-insights-widget"
      className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
      style={{ borderLeftColor: borderColor, borderLeftWidth: 3 }}
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-overline text-amber-500/80 mb-1 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Studio Insights · next {data?.window?.days || 14} days
          </div>
          <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
            {insights.length} thing{insights.length === 1 ? "" : "s"} worth a look
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Click any row to open Studio focused on that person and date.
          </p>
        </div>
        <button
          type="button"
          data-testid="studio-insights-widget-open-all"
          onClick={() => navigate("/studio")}
          className="text-[11px] font-mono uppercase tracking-wide text-zinc-400 hover:text-amber-500 transition-colors"
        >
          Open Studio →
        </button>
      </div>

      <ul className="space-y-1.5" data-testid="studio-insights-widget-list">
        {insights.map((ins) => {
          const meta = KIND_META[ins.kind] || KIND_META.stack;
          const Icon = meta.icon;
          return (
            <li key={ins.id}>
              <button
                type="button"
                data-testid={`studio-insights-widget-row-${ins.kind}-${ins.user_id}`}
                onClick={() => openStudio(ins)}
                className="group w-full text-left flex items-center gap-3 py-2 px-2 rounded-sm hover:bg-zinc-900/60 transition-colors"
              >
                <span
                  className={`inline-flex items-center justify-center w-6 h-6 rounded-sm shrink-0 ${meta.bg} ${meta.tint}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <span className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{ins.message}</div>
                  <div className="text-[10px] text-zinc-500 font-mono truncate">
                    {ins.hint}
                  </div>
                </span>
                {ins.iso_date && (
                  <span className="text-[10px] text-zinc-500 font-mono shrink-0 hidden sm:inline">
                    {ins.iso_date}
                  </span>
                )}
                <ChevronRight className="w-3.5 h-3.5 text-zinc-700 group-hover:text-amber-400 transition-colors shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>
    </article>
  );
}
